import { useEffect, useRef } from "react";

type Props = {
  /** When this key changes, a burst is fired. */
  trigger: number | string | boolean;
  /** Bounding box for the burst (pixels). Defaults to 320x180. */
  width?: number;
  height?: number;
  /** Number of pieces. Keep small for a gentle feel. */
  count?: number;
  colors?: string[];
};

/**
 * Confetti — gentle canvas-based confetti burst.
 * - No dependencies.
 * - Honors prefers-reduced-motion (renders nothing when reduced).
 * - Overlays absolutely; parent should be `relative`.
 */
export function Confetti({
  trigger,
  width = 320,
  height = 180,
  count = 60,
  colors = ["#1E5EFF", "#7FA6FF", "#B8CCFF", "#FFC542", "#FF8FB1"],
}: Props) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const rafRef = useRef<number | null>(null);

  useEffect(() => {
    if (typeof window === "undefined") return;
    if (window.matchMedia?.("(prefers-reduced-motion: reduce)").matches) return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    canvas.width = width * dpr;
    canvas.height = height * dpr;
    ctx.scale(dpr, dpr);

    type Piece = {
      x: number;
      y: number;
      vx: number;
      vy: number;
      rot: number;
      vr: number;
      size: number;
      color: string;
      shape: "rect" | "circle";
      life: number;
    };

    const cx = width / 2;
    const cy = height * 0.55;
    const pieces: Piece[] = Array.from({ length: count }).map(() => {
      const angle = -Math.PI / 2 + (Math.random() - 0.5) * Math.PI * 0.9;
      const speed = 2 + Math.random() * 2.4;
      return {
        x: cx + (Math.random() - 0.5) * 20,
        y: cy,
        vx: Math.cos(angle) * speed,
        vy: Math.sin(angle) * speed - 0.6,
        rot: Math.random() * Math.PI * 2,
        vr: (Math.random() - 0.5) * 0.25,
        size: 4 + Math.random() * 4,
        color: colors[Math.floor(Math.random() * colors.length)],
        shape: Math.random() > 0.35 ? "rect" : "circle",
        life: 0,
      };
    });

    const gravity = 0.08;
    const drag = 0.992;
    const maxLife = 90; // ~1.5s at 60fps

    let running = true;
    const tick = () => {
      if (!running) return;
      ctx.clearRect(0, 0, width, height);
      let alive = 0;
      for (const p of pieces) {
        p.life++;
        if (p.life > maxLife) continue;
        alive++;
        p.vy += gravity;
        p.vx *= drag;
        p.vy *= drag;
        p.x += p.vx;
        p.y += p.vy;
        p.rot += p.vr;
        const fade = Math.max(0, 1 - (p.life - maxLife * 0.6) / (maxLife * 0.4));
        ctx.save();
        ctx.globalAlpha = fade;
        ctx.translate(p.x, p.y);
        ctx.rotate(p.rot);
        ctx.fillStyle = p.color;
        if (p.shape === "rect") {
          ctx.fillRect(-p.size / 2, -p.size / 3, p.size, p.size * 0.6);
        } else {
          ctx.beginPath();
          ctx.arc(0, 0, p.size / 2, 0, Math.PI * 2);
          ctx.fill();
        }
        ctx.restore();
      }
      if (alive > 0) {
        rafRef.current = requestAnimationFrame(tick);
      } else {
        ctx.clearRect(0, 0, width, height);
      }
    };
    rafRef.current = requestAnimationFrame(tick);

    return () => {
      running = false;
      if (rafRef.current != null) cancelAnimationFrame(rafRef.current);
    };
  }, [trigger, width, height, count, colors]);

  return (
    <canvas
      ref={canvasRef}
      aria-hidden="true"
      className="pointer-events-none absolute left-1/2 top-0 -translate-x-1/2"
      style={{ width, height }}
    />
  );
}
